import type {
    ApplicationSettingThemeColor,
    ArgumentsSchema,
    AVPlayUrlResponse,
    BaseCellValue,
    DataSourceAbstract,
    EVENT_VARIABLE_TYPE,
    Field,
    FieldADTValue,
    FieldCellValue,
    ImageInfo,
    InnerTypeADTValue,
    PageAbstract,
    PageBlockContextIndex,
    RecordLikeProtocol,
    RichTextContentProtocol,
    SelectDataSourceVariable,
    StateSchema,
    SubProcessResolvedArg,
    TypeInstanceMap,
    VariableADTvalue
} from '@lighthouse/core'
import { VariableType, WechatPayType } from '@lighthouse/core'
import { isMiniprogram, isWechatBrowser, safeToArray, safeToNumber, safeToString } from '@lighthouse/tools'
import type { JSONContent } from '@tiptap/core'
import { lightFormat } from 'date-fns'
import { find } from 'rambda'

import { addresseeFieldType, defaultDateFormat, resourceField } from '../../../constants'
import type { FlowNodeResultADT, HandleSubProcessActionConfig, OtherPlatformItem, Workflow } from '../../../types'
import { getInitInnerTypeADTValue } from '../../field'
import { isMobileDevice } from '../../getApplicationPreviewType'
import { getImageFullUrlInApplication } from '../../image'
import type { ParseVariable2FieldOption, ResolvedFilterExtraParams } from '../filterResolver'

export const waitFor = (ms: number) =>
    new Promise(resolve => {
        setTimeout(resolve, ms)
    })

export const getRecordResult = (result: undefined | FlowNodeResultADT) => {
    if (!result) {
        return
    }

    if (result.type === 'record' && result.success) {
        return result.data
    }
}

export const getArgsResult = (result: undefined | FlowNodeResultADT) => {
    if (!result) {
        return
    }

    if (result.type === 'args' && result.success) {
        return result.data
    }
}

// type FormatPayload = {
//     dateFormat?: boolean
//     fileFormat?: boolean
// }

export type GenerateVariableValueOptions = {
    format?: boolean
    useFileUrl?: boolean
}

export interface ExtraParams {
    appId: string
    envId: string
    userId: string | undefined
    userRecord?: RecordLikeProtocol
    pageList?: PageAbstract[]
    formRecord?: RecordLikeProtocol
    viewRecord?: RecordLikeProtocol
    /** 从内到外的自定义视图。视图控制器的实例记录是上一层的 recordId。 */
    customViewStack?: { viewId: string; recordId?: string }[]
    imageInfo?: ImageInfo
    pageBlockContextIndex?: PageBlockContextIndex | null
    dataSourceList?: DataSourceAbstract[]
    pageStateParameterValues?: StateSchema[]
    pageQueryParameterValues?: ArgumentsSchema[]
    palettes?: ApplicationSettingThemeColor[]
    parseVariable2Field: (value: VariableADTvalue, options?: ParseVariable2FieldOption) => FieldADTValue | undefined
    parseSelectDataSourceVariable2Field?: (
        variable: SelectDataSourceVariable,
        extraParams: ResolvedFilterExtraParams
    ) => Promise<FieldADTValue | undefined>
    // workflow
    workflow?: Workflow
    triggerArgs?: SubProcessResolvedArg[]
    upstreamNodesResult?: Record<string, FlowNodeResultADT>
    workflowList?: Workflow[]
    otherPlatformList?: OtherPlatformItem[]
    scope?: string
    // event params
    pageName?: string
    blockName?: string
    event?: { eventType?: EVENT_VARIABLE_TYPE; eventName?: string }
    fetchAVPlayUrl?: (originalUrl: string, isOrigin?: boolean) => Promise<AVPlayUrlResponse>
}

export const findCustomNodesInJson = (content: JSONContent): VariableADTvalue[] => {
    const result: VariableADTvalue[] = []
    let firstSearch = true
    const search = (node: JSONContent) => {
        if (node) {
            if (node.type === 'paragraph' || node.type === 'hardBreak') {
                if (!firstSearch) {
                    result.push({
                        type: VariableType.VALUE,
                        valueVariable: {
                            type: 'text',
                            value: '\n'
                        }
                    })
                }
                firstSearch = false
            }
            if (node.type === 'variable' && node.attrs?.value) {
                result.push(node.attrs.value as VariableADTvalue)
            }
            if (node.type === 'text' && node.text) {
                result.push({
                    type: VariableType.VALUE,
                    valueVariable: {
                        type: 'text',
                        value: node.text
                    }
                })
            }
            if (node.content) {
                node.content.forEach(child => {
                    search(child)
                })
            }
        }
    }
    search(content)
    return result
}

export const transformValueByFormat = (params: {
    appId: string | undefined
    field?: Field
    value: FieldCellValue | undefined
    format?: boolean
    useFileUrl?: boolean
}) => {
    const { appId, field, value, useFileUrl, format } = params
    if (format && field?.innerType === 'DATE') {
        const dateFormat = field.type === 'date' ? field.date?.format || defaultDateFormat : defaultDateFormat
        return typeof value === 'number' && !Number.isNaN(value) ? lightFormat(value, dateFormat) : value
    }
    if (appId && useFileUrl && field && resourceField.has(field.type) && Array.isArray(value)) {
        return value.map(item => {
            return typeof item === 'string' ? getImageFullUrlInApplication(appId, item) : item
        })
    }
    return value
}

export const modifyVariableValue = (params: {
    node: VariableADTvalue
    extraParams?: ExtraParams
    options?: GenerateVariableValueOptions
}) => {
    const { node, extraParams, options } = params
    const { appId, viewRecord, formRecord, imageInfo, upstreamNodesResult, scope, parseVariable2Field } = extraParams || {}
    // if (node.type === VariableType.USER && userRecord) {
    //     const { fieldId = '' } = node.userVariable || {}
    //     return userRecord?.content?.[fieldId]?.value
    // }

    const fieldValue = parseVariable2Field?.(node, { viewRecord, formRecord, upstreamNodesResult, imageInfo, scope })
    return transformValueByFormat({
        appId,
        field: fieldValue as Field,
        value: fieldValue?.value,
        ...options
    })
}

export const generateVariableValue = (params: {
    innerType: TypeInstanceMap
    jsonContent: JSONContent
    extraParams?: ExtraParams
    // isRichText?: boolean
    options?: GenerateVariableValueOptions
}): BaseCellValue | undefined => {
    const { innerType, jsonContent, extraParams, options } = params
    const variableNodes = findCustomNodesInJson(jsonContent)
    const values = variableNodes.map(node => modifyVariableValue({ node, extraParams, options }))
    // NULL: 直接返回未定义
    // ARRAY: 不会存在富文本其他节点，只需取出第一个节点解析
    // DATE: 同上
    // BOOL：同上
    // NUMBER: 可能存在其他节点，需要拼接
    // 否则直接拼接其他的富文本节点
    switch (innerType) {
        case 'NULL': {
            return
        }
        case 'ARRAY': {
            const v = values[0]
            if (Array.isArray(v)) {
                return (v as unknown[]).filter(Boolean).map(String)
            }
            if (typeof v === 'string') {
                return v.split(',')
            }
            if (typeof v === 'number') {
                return [String(v)]
            }
            return []
        }
        case 'DATE': {
            const v = values[0]
            if (!v && v !== 0) {
                return Number.NaN
            }
            return Number(values[0])
        }

        case 'RICH_TEXT': {
            return values?.[0]
        }
        case 'BOOL': {
            return !!values[0]
        }
        case 'NUMBER': {
            const numList = values.filter(v => v !== undefined)
            const numString = values.join('')
            return numList.length > 0 ? (numString ? Number(numString) : undefined) : undefined
        }

        default: {
            return values.join('')
        }
    }
}

export const asyncModifyVariableValue = async (params: {
    node: VariableADTvalue
    extraParams: ExtraParams
    options?: GenerateVariableValueOptions
}) => {
    const { node, extraParams, options } = params
    const {
        appId,
        viewRecord,
        upstreamNodesResult,
        formRecord,
        imageInfo,
        scope,
        parseVariable2Field,
        parseSelectDataSourceVariable2Field
    } = extraParams || {}

    if (node.type === VariableType.SELECT_DATASOURCE) {
        if (!node.selectDataSourceVariable) {
            return
        }
        const data = await parseSelectDataSourceVariable2Field?.(node, extraParams)
        // const args = getArgsResult(extraParams?.upstreamNodesResult?.[nodeId])
        return data?.value
    }
    const fieldValue = parseVariable2Field?.(node, { viewRecord, formRecord, upstreamNodesResult, imageInfo, scope })
    return transformValueByFormat({
        appId,
        field: fieldValue as Field,
        value: fieldValue?.value,
        ...options
    })
}

export const transformValueByInnerType = (params: { innerType: TypeInstanceMap; value: BaseCellValue | undefined | null }) => {
    const { innerType, value } = params
    if (value === undefined) {
        return value
    }
    if (innerType === 'DATE' || innerType === 'NUMBER') {
        return safeToNumber(value)
    }
    if (innerType === 'BOOL') {
        return !!value
    }
    if (innerType === 'TEXT') {
        return safeToString(value)
    }
    if (innerType === 'ARRAY') {
        return safeToArray(value)
    }
    return value
}

export const asyncGenerateVariableValue = async (params: {
    innerType: TypeInstanceMap
    jsonContent: JSONContent
    extraParams: ExtraParams
    // isRichText?: boolean
    options?: GenerateVariableValueOptions
}): Promise<BaseCellValue | undefined> => {
    const { innerType, jsonContent, extraParams, options } = params
    const variableNodes = findCustomNodesInJson(jsonContent)
    const values = await Promise.all(variableNodes.map(node => asyncModifyVariableValue({ node, extraParams, options })))
    // NULL: 直接返回未定义
    // ARRAY: 不会存在富文本其他节点，只需取出第一个节点解析
    // DATE: 同上
    // BOOL：同上
    // NUMBER: 可能存在其他节点，需要拼接
    // 否则直接拼接其他的富文本节点
    switch (innerType) {
        case 'NULL': {
            return
        }
        case 'ARRAY': {
            const v = values[0]
            if (Array.isArray(v)) {
                return (v as unknown[]).filter(Boolean).map(String)
            }
            if (typeof v === 'string') {
                return v.split(',')
            }
            if (typeof v === 'number') {
                return [String(v)]
            }
            return []
        }
        case 'DATE': {
            return values[0] === undefined ? undefined : Number(values[0])
        }
        case 'BOOL': {
            return !!values[0]
        }
        case 'NUMBER': {
            const numList = values.filter(v => v !== undefined)
            const numString = values.join('')
            return numList.length > 0 ? (numString ? Number(numString) : undefined) : undefined
        }

        default: {
            return values.join('')
        }
    }
}

const generatePersonVariableValue = (fieldId: string, record: RecordLikeProtocol, dataSourceList: DataSourceAbstract[]) => {
    const { dsId, content } = record
    const ds = find(item => item.id === dsId, dataSourceList)
    if (!ds) {
        return undefined
    }
    const { schema } = ds
    const field = schema[fieldId]
    if (!field) {
        return undefined
    }
    const cellValue = content[fieldId]?.value
    if (!cellValue) {
        return undefined
    }
    if (addresseeFieldType.has(field.type)) {
        return {
            type: VariableType.VALUE,
            valueVariable: {
                type: field.type,
                value: cellValue
            }
        } as VariableADTvalue
    }
    if (field.innerType === 'ARRAY') {
        return {
            type: VariableType.VALUE,
            valueVariable: {
                type: 'person',
                value: cellValue
            }
        } as VariableADTvalue
    }
    if (field.innerType === 'TEXT') {
        const textValue = safeToString(cellValue)
        return {
            type: VariableType.VALUE,
            valueVariable: {
                type: 'person',
                value: textValue.split(',')
            }
        } as VariableADTvalue
    }
}

export const resolvedUpstreamVariableValue = (params: { data: VariableADTvalue[]; extraParams: ExtraParams }): VariableADTvalue[] => {
    const { data, extraParams } = params
    const { parseVariable2Field, dataSourceList, upstreamNodesResult } = extraParams
    // const dataSourceList: DataSourceAbstract[] = extraParams?.dataSourceList || []
    return data.reduce<VariableADTvalue[]>((prev, cur) => {
        if (cur.type === VariableType.UPSTREAM) {
            const nodeId = cur.upstreamVariable?.nodeId
            const fieldId = cur.upstreamVariable?.fieldId
            if (!nodeId || !fieldId) {
                return prev
            }
            const record = getRecordResult(upstreamNodesResult?.[nodeId])
            if (!record) {
                return prev
            }
            const v = generatePersonVariableValue(fieldId, record, dataSourceList || [])
            if (!v) {
                return prev
            }
            prev.push(v)
            return prev
        }
        if (cur.type === VariableType.USER) {
            const scope = extraParams?.scope
            const fieldValue = parseVariable2Field(cur, { scope })
            if (!fieldValue) {
                return prev
            }
            if (addresseeFieldType.has(fieldValue.type)) {
                const v = {
                    type: VariableType.VALUE,
                    valueVariable: {
                        type: fieldValue.type,
                        value: fieldValue.value
                    }
                } as VariableADTvalue
                prev.push(v)
                return prev
            }
            if (fieldValue.innerType === 'ARRAY') {
                const v = {
                    type: VariableType.VALUE,
                    valueVariable: {
                        type: 'person',
                        value: fieldValue.value
                    }
                } as VariableADTvalue
                prev.push(v)
                return prev
            }
            if (fieldValue.innerType === 'TEXT') {
                const textValue = safeToString(fieldValue.value)
                const v = {
                    type: VariableType.VALUE,
                    valueVariable: {
                        type: 'person',
                        value: textValue.split(',')
                    }
                } as VariableADTvalue
                prev.push(v)
                return prev
            }
        }
        prev.push(cur)
        return prev
    }, [])
}

export const getHandleSubProcessVariableValue = (config: HandleSubProcessActionConfig, extraParams: ExtraParams) => {
    const { args } = config
    return args?.map(item => ({
        ...item,
        value: item.value ? generateVariableValue({ innerType: item.innerType, jsonContent: item.value, extraParams }) : undefined
    }))
}

/**
 *  变量解析
 * @param variable 变量
 * @param extraParams
 * @returns
 */
export const resolveVariable = (variable: VariableADTvalue, extraParams?: ExtraParams): FieldCellValue | undefined => {
    if (!variable) {
        return
    }

    switch (variable.type) {
        case VariableType.UPSTREAM: {
            const { upstreamVariable } = variable

            if (!upstreamVariable || !upstreamVariable.nodeId || !upstreamVariable.fieldId) {
                return
            }

            const { nodeId, fieldId } = upstreamVariable
            const record = getRecordResult(extraParams?.upstreamNodesResult?.[nodeId])
            return record?.content?.[fieldId]?.value
        }
        case VariableType.VALUE: {
            const { valueVariable } = variable

            if (!valueVariable) {
                return
            }

            const { type, value } = valueVariable

            return value
        }

        default: {
            const viewRecord = extraParams?.viewRecord
            const formRecord = extraParams?.formRecord
            const scope = extraParams?.scope
            const upstreamNodesResult = extraParams?.upstreamNodesResult
            const imageInfo = extraParams?.imageInfo
            const fieldValue = extraParams?.parseVariable2Field(variable, {
                viewRecord,
                formRecord,
                scope,
                upstreamNodesResult,
                imageInfo
            })

            if (!fieldValue || fieldValue.innerType === 'NULL') {
                return
            }

            return fieldValue.value
        }
    }
}

/**
 * 「第几个面板」配置可存变量 ADT（或历史的纯 number）；执行控制动作时需解析为 finite number 供容器计算目标面板。
 */
export const resolveNthPanelParam = (
    nthPanel: VariableADTvalue | number | undefined | null,
    extraParams?: ExtraParams
): number | undefined => {
    if (nthPanel == null) {
        return
    }
    if (typeof nthPanel === 'number') {
        return Number.isFinite(nthPanel) ? nthPanel : undefined
    }
    const raw = resolveVariable(nthPanel, extraParams)
    if (raw === null || raw === undefined) {
        return
    }
    const n = Number(raw)
    return Number.isFinite(n) ? n : undefined
}

/**
 * ------------------  runner action engine  ------------------
 */
export const getWechatPayType = function () {
    const isMobile = isMobileDevice()
    const isWechat = isWechatBrowser()

    if (isMiniprogram()) {
        return WechatPayType.MINIPROGRAM
    }

    if (isMobile) {
        if (isWechat) {
            return WechatPayType.JSAPI
        }
        return WechatPayType.H5
    }
    return WechatPayType.NATIVE
}

export const getSearchParams = function (params: Record<string, string | number | boolean>) {
    return Object.entries(params).reduce((pre, [k, v]) => (v === undefined ? pre : `${pre}${pre === '' ? '' : '&'}${k}=${v}`), '')
}

export const getInnerTypeADTValueByStateSchema = (item: StateSchema, extraParams?: ExtraParams): InnerTypeADTValue => {
    if (!item.defaultValue) {
        return getInitInnerTypeADTValue(item.innerType)
    }
    const val = generateVariableValue({ innerType: item.innerType, jsonContent: item.defaultValue, extraParams })
    if (val === undefined) {
        return getInitInnerTypeADTValue(item.innerType)
    }
    return {
        innerType: item.innerType,
        value: val
    } as InnerTypeADTValue
}
